import type { ClienteAchado, ResultadoDaBusca } from '@entregas/core';
import type { Db } from './cliente';
import type { Database } from './database.types';
import { exigir } from './erros';
import { paraPagamentos } from './mapeadores';

// Busca "/" do painel: uma chamada à função do banco (buscar_no_balcao), que já devolve agrupado por tipo
// (pedido pelo nº, cliente pelo telefone ou nome, endereço um por cliente), só das lojas de quem busca.

type LinhaGerada = Database['public']['Functions']['buscar_no_balcao']['Returns'][number];
/** O gerador de tipos põe as colunas de função como não nulas; aqui cada tipo só preenche as suas. */
type LinhaDaBusca = { [K in keyof LinhaGerada]: LinhaGerada[K] | null };

function cliente(l: LinhaDaBusca): ClienteAchado {
  return {
    id: l.cliente_id ?? '',
    nome: l.cliente_nome ?? '',
    telefone: l.cliente_telefone ?? '',
    pedidos: l.cliente_pedidos ?? 0,
    ultimoPedidoEm: l.cliente_ultimo_pedido_em,
  };
}

/** Linha da função → resultado. Tipo desconhecido (função mais nova que o app) vira null e é ignorado. */
export function paraResultadoDaBusca(l: LinhaDaBusca): ResultadoDaBusca | null {
  if (l.tipo === 'pedido' && l.pedido_id && l.pedido_numero != null && l.pedido_status) {
    return {
      tipo: 'pedido',
      cliente: cliente(l),
      pedido: {
        id: l.pedido_id,
        numero: l.pedido_numero,
        status: l.pedido_status,
        criadoEm: l.pedido_criado_em ?? '',
        dia: l.pedido_dia ?? '',
        itens: l.pedido_itens ?? '',
        totalCentavos: l.pedido_total_centavos ?? 0,
        pagamentos: paraPagamentos(l.pedido_pagamentos),
        entregadorNome: l.entregador_nome,
      },
    };
  }
  if (l.tipo === 'cliente') return { tipo: 'cliente', cliente: cliente(l) };
  if (l.tipo === 'endereco' && l.endereco_id) {
    return {
      tipo: 'endereco',
      cliente: cliente(l),
      endereco: { id: l.endereco_id, rua: l.endereco_rua ?? '', bairro: l.endereco_bairro },
    };
  }
  return null;
}

/** Até `limite` (1 a 20) por grupo. Texto vazio não chama o banco. */
export async function buscarNoBalcao(
  db: Db,
  texto: string,
  limite = 8,
): Promise<ResultadoDaBusca[]> {
  const termo = texto.trim();
  if (!termo) return [];
  const linhas: LinhaDaBusca[] = exigir(
    await db.rpc('buscar_no_balcao', { p_texto: termo, p_limite: limite }),
  );
  return linhas.flatMap((l) => {
    const r = paraResultadoDaBusca(l);
    return r ? [r] : [];
  });
}
